import React from "react";
import {
  Alert,
  Box,
  Paper,
  Typography,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import { ACTIVITY_TYPE_NEW_USER_GIFT } from "@/api";
import { colors, festiveCardSx } from "./shared/constants";
import { ActivityPageBase } from "./shared/ActivityPageBase";
import { SuccessCard } from "./shared/SuccessCard";

const GIFT_SIZE_LABEL = "200MB";
const DEFAULT_SIZE_LABEL = "56MB";
const TOTAL_SIZE_LABEL = "256MB";

function StorageBlock({ label, value, color, bgColor, glow }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: bgColor,
        border: `1px solid ${color}55`,
        borderRadius: 3,
        px: { xs: 2.25, sm: 2.75 },
        py: 2,
        minWidth: { xs: 104, sm: 120 },
        boxShadow: glow ? `0 0 26px ${colors.goldGlow}` : "none",
      }}
    >
      <StorageIcon sx={{ color, fontSize: 28, mb: 0.5 }} />
      <Typography fontWeight={900} sx={{ color, fontSize: "1.35rem", lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: "rgba(255, 248, 231, 0.72)", mt: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );
}

function GiftCardContent({ message, claimError }) {
  return (
    <Paper elevation={0} sx={{ ...festiveCardSx, p: { xs: 3, sm: 4 } }}>
      {/* Corner decorations */}
      <Box
        sx={{
          position: "absolute", top: 0, left: 0, width: 76, height: 76,
          borderTop: `3px solid ${colors.deepGold}`,
          borderLeft: `3px solid ${colors.deepGold}`,
          borderTopLeftRadius: 20, opacity: 0.9,
        }}
      />
      <Box
        sx={{
          position: "absolute", top: 0, right: 0, width: 76, height: 76,
          borderTop: `3px solid ${colors.deepGold}`,
          borderRight: `3px solid ${colors.deepGold}`,
          borderTopRightRadius: 20, opacity: 0.9,
        }}
      />

      {/* Storage before/after blocks */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 1.5, sm: 2.5 },
          mb: 4,
          flexWrap: "wrap",
        }}
      >
        <StorageBlock
          label="开通前"
          value={DEFAULT_SIZE_LABEL}
          color={colors.ivory}
          bgColor="rgba(255, 248, 231, 0.08)"
        />
        <Box textAlign="center" sx={{ px: 0.5 }}>
          <Typography
            sx={{
              color: colors.gold,
              fontSize: { xs: "1.35rem", sm: "1.6rem" },
              fontWeight: 900,
              lineHeight: 1,
              textShadow: "0 0 18px rgba(255, 209, 102, 0.35)",
            }}
          >
            +{GIFT_SIZE_LABEL}
          </Typography>
          <Typography sx={{ fontSize: "1.45rem", mt: 0.5, color: colors.ivory }}>
            →
          </Typography>
        </Box>
        <StorageBlock
          label="领取后，能下载50+优质文档"
          value={TOTAL_SIZE_LABEL}
          color={colors.gold}
          bgColor="rgba(255, 209, 102, 0.12)"
          glow
        />
      </Box>

      {/* Highlight banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, rgba(255, 238, 186, 0.18), rgba(255, 209, 102, 0.1))",
          border: "1px solid rgba(255, 233, 156, 0.32)",
          borderRadius: 3,
          px: 2, py: 1.4, mb: 3,
        }}
      >
        <Typography sx={{ color: colors.gold, fontWeight: 700, textAlign: "center" }}>
          新用户首份礼包：存储空间从 56MB 点亮至 256MB
        </Typography>
      </Box>

      {/* Bullet list */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
        {[
          "✨ 新注册用户专属，喜迎开通礼",
          `✨ 立即获赠 ${GIFT_SIZE_LABEL} 云端存储空间`,
          "✨ 礼包到账后可立即使用",
          "✨ 每个账号仅可领取一次",
        ].map((item) => (
          <Typography
            key={item}
            variant="body2"
            sx={{ color: "rgba(255, 248, 231, 0.88)", fontSize: "0.94rem" }}
          >
            {item}
          </Typography>
        ))}
      </Box>

      {message && (
        <Typography sx={{ color: "rgba(255, 248, 231, 0.76)", mb: 2.5, textAlign: "center" }}>
          {message}
        </Typography>
      )}

      {claimError && (
        <Alert
          severity="error"
          sx={{
            mb: 2, borderRadius: 2.5,
            color: colors.ivory,
            background: "rgba(81, 8, 20, 0.72)",
            border: "1px solid rgba(255, 209, 102, 0.18)",
            "& .MuiAlert-icon": { color: colors.gold },
          }}
        >
          {claimError}
        </Alert>
      )}
    </Paper>
  );
}

export default function NewUserGiftPage() {
  return (
    <ActivityPageBase
      activityType={ACTIVITY_TYPE_NEW_USER_GIFT}
      pageTitle="新用户专属福利 - 知嘛"
      renderCardContent={({ message, claimError }) => (
        <GiftCardContent message={message} claimError={claimError} />
      )}
      renderSuccessCard={({ claimedAt, message }) => (
        <SuccessCard
          claimedAt={claimedAt}
          message={message}
          rewardLabel={`+${GIFT_SIZE_LABEL} 存储空间`}
          footerHint="请返回知嘛小程序查看最新存储空间"
        />
      )}
      claimButtonLabel="立即领取，下载优质资源"
      claimingLabel="正在拆开礼包…"
    />
  );
}
